import React, { useCallback, useMemo } from "react";
import type { AgendaConsultant, AgendaDayPayload, AgendaWeekSummary } from "../../agendaTypes";
import {
  buildConsultantMobileTimeline,
  findNextConsultantVisit,
  formatMobileDayListHeading,
  isSelectedDateTodayInFortaleza,
} from "../../mobileConsultantAgendaModel";
import ScheduleType from "../../ScheduleType";
import { countConsultantDayStats } from "../../scheduleGridModel";
import { isoDateToDisplayDate } from "../../agendaDateFormat";
import { useAgendaWeatherContext } from "../../context/AgendaWeatherContext";
import { AGENDA_FEATURE_FLAGS } from "../../constants/agendaFeatureFlags";
import type { AgendaWeatherAlert } from "../../agendaWeatherTypes";
import type { CityWeather } from "../../agendaWeatherTypes";
import MobileAgendaHeader from "./MobileAgendaHeader";
import MobileWeekStrip from "./MobileWeekStrip";
import NextVisitCard from "./NextVisitCard";
import MobileScheduleList from "./MobileScheduleList";
import MobileWeatherVisitAlert from "./MobileWeatherVisitAlert";
import {
  updateScheduleVisitOutcome,
  type ScheduleVisitOutcome,
} from "../../../../services/scheduleVisitOutcome";
import { useToast } from "../../../../hooks/toast";
import { extractApiErrorMessage } from "../../../../utils/scheduleLockPolicy";
import { AgendaMobileListSkeleton } from "../AgendaSkeleton";

type MobileConsultantAgendaProps = {
  userFirstName: string;
  consultant: AgendaConsultant;
  dayData: AgendaDayPayload;
  weekSummary: AgendaWeekSummary;
  selectedDateIso: string;
  loadingDay: boolean;
  canManageSchedules: boolean;
  weatherAlerts: AgendaWeatherAlert[];
  onSelectDate: (isoDate: string) => void;
  onOpenVisit: (coord: string, scheduleId?: number) => void;
  onScheduleFreeSlot: (coord: string) => void;
  /** Unlock a blocked slot; omitted when the viewer cannot change this agenda. */
  onUnlockSlot?: (coord: string) => void;
  onBulkBlock: () => void;
  headerTitle?: string;
  beforeWeekStrip?: React.ReactNode;
  weatherCityOverride?: string;
  weatherCityWeatherOverride?: CityWeather | null;
  onVisitOutcomeUpdated: () => void;
  detailPanel: React.ReactNode;
};

const MobileConsultantAgenda: React.FC<MobileConsultantAgendaProps> = ({
  userFirstName,
  consultant,
  dayData,
  weekSummary,
  selectedDateIso,
  loadingDay,
  canManageSchedules,
  weatherAlerts,
  onSelectDate,
  onOpenVisit,
  onScheduleFreeSlot,
  onUnlockSlot,
  onBulkBlock,
  headerTitle = "Minha agenda",
  beforeWeekStrip,
  weatherCityOverride,
  weatherCityWeatherOverride,
  onVisitOutcomeUpdated,
  detailPanel,
}) => {
  const { addToast } = useToast();
  const agendaWeather = useAgendaWeatherContext();
  const displayDate = isoDateToDisplayDate(selectedDateIso);

  const timeline = useMemo(
    () =>
      buildConsultantMobileTimeline({
        dateIso: selectedDateIso,
        consultantId: consultant.id,
        businessHours: dayData.business_hours,
        schedules: dayData.schedules,
      }),
    [consultant.id, dayData.business_hours, dayData.schedules, selectedDateIso]
  );

  const nextVisit = useMemo(
    () =>
      findNextConsultantVisit({
        dateIso: selectedDateIso,
        consultantId: consultant.id,
        businessHours: dayData.business_hours,
        schedules: dayData.schedules,
      }),
    [consultant.id, dayData.business_hours, dayData.schedules, selectedDateIso]
  );

  const dayStats = useMemo(
    () =>
      countConsultantDayStats(
        dayData.schedules,
        displayDate,
        consultant.id,
        dayData.business_hours
      ),
    [consultant.id, dayData.business_hours, dayData.schedules, displayDate]
  );

  const nextRow = nextVisit
    ? timeline.find((row) => row.coord === nextVisit.coord)
    : null;

  const cityWeather = weatherCityWeatherOverride ?? agendaWeather.primaryCityWeather;
  const weatherCityName =
    weatherCityOverride ?? agendaWeather.location?.city ?? cityWeather?.cityName ?? "";

  const contextualAlert = useMemo(() => {
    if (!AGENDA_FEATURE_FLAGS.rainAlerts || weatherAlerts.length === 0) {
      return null;
    }
    const relevant = weatherAlerts.find((alert) =>
      alert.scheduleIds.some((id) =>
        dayData.schedules.some(
          (row) =>
            row.consultant_id === consultant.id &&
            row.id === id &&
            row.status === ScheduleType.SCHEDULED
        )
      )
    );
    return relevant ?? null;
  }, [consultant.id, dayData.schedules, weatherAlerts]);

  const listHeading = formatMobileDayListHeading(selectedDateIso);
  const isToday = isSelectedDateTodayInFortaleza(selectedDateIso);

  const handleMarkOutcome = useCallback(
    async (
      row: (typeof timeline)[number],
      outcome: ScheduleVisitOutcome,
      onlyScheduleIds?: number[]
    ) => {
      // A client picked in the strip is marked alone; otherwise the whole hour.
      const scheduleIds = Array.from(
        new Set(
          (onlyScheduleIds ?? row.appointments.map((appointment) => appointment.id)).filter(
            (id) => Number.isFinite(id) && id > 0
          )
        )
      );

      if (scheduleIds.length === 0 && row.primaryScheduleId > 0) {
        scheduleIds.push(row.primaryScheduleId);
      }

      try {
        await updateScheduleVisitOutcome(scheduleIds, outcome);
        addToast({
          title:
            outcome === "completed"
              ? "Visita marcada como realizada"
              : "Visita marcada como não veio",
          type: "success",
        });
        onVisitOutcomeUpdated();
      } catch (error: unknown) {
        addToast({
          title:
            extractApiErrorMessage(error) ??
            "Não foi possível atualizar o resultado da visita.",
          type: "error",
        });
      }
    },
    [addToast, onVisitOutcomeUpdated]
  );

  return (
    <div className="mx-auto w-full max-w-lg overflow-x-hidden px-4 pb-6 pt-1">
      <MobileAgendaHeader
        userFirstName={userFirstName}
        onBlockSlots={onBulkBlock}
        canManageSchedules={canManageSchedules}
        title={headerTitle}
      />

      {beforeWeekStrip}

      <MobileWeekStrip
        selectedDateIso={selectedDateIso}
        weekSummary={weekSummary}
        onSelectDate={onSelectDate}
      />


      {contextualAlert ? (
        <MobileWeatherVisitAlert
          alert={contextualAlert}
          clientHint={
            nextVisit
              ? `Sua visita pode ser afetada no horário previsto.`
              : null
          }
        />
      ) : null}

      {!loadingDay && nextVisit && nextRow ? (
        <NextVisitCard
          visit={nextVisit}
          selectedDateIso={selectedDateIso}
          weatherCityName={weatherCityName}
          cityWeather={cityWeather}
          clientLabel={nextRow.clientLabel}
          onOpenDetails={() => onOpenVisit(nextVisit.coord, nextVisit.schedule.id)}
        />
      ) : null}

      {!loadingDay && !nextVisit && isToday && dayData.has_open_hours ? (
        <p className="mb-4 rounded-xl border border-[#E2E8F0] bg-[#F7FAFC] px-4 py-3 text-sm text-[#718096]">
          Nenhuma outra visita agendada para hoje
        </p>
      ) : null}

      {loadingDay ? (
        <AgendaMobileListSkeleton />
      ) : !dayData.has_open_hours ? (
        <p className="rounded-xl border border-dashed border-[#E2E8F0] bg-white px-4 py-6 text-center text-sm text-[#718096]">
          Nenhum horário aberto para este dia
        </p>
      ) : (
        <MobileScheduleList
          heading={listHeading}
          rows={timeline}
          visitsCount={dayStats.visits}
          freeSlotsCount={dayStats.freeSlots}
          onPressVisit={onOpenVisit}
          onPressFreeSlot={onScheduleFreeSlot}
          onUnlockSlot={onUnlockSlot}
          onMarkOutcome={handleMarkOutcome}
        />
      )}

      {detailPanel}
    </div>
  );
};

export default MobileConsultantAgenda;
